import React from 'react';

import PartialTextWithIcon from '../partials/twb_text_with_icon';
import PartialText from '../partials/twb_text';
import PartialFeatures from '../partials/twb_features';
import TwbCTA from '../partials/twb_cta';
import TableSimple from '../table_simple';
import ContactFormWrapper from '../contact_form_wrapper';

import PartialCTA from '../partials/cta';
import { useViewport } from '~/utils/viewport-provider';
import Checkmarks from '../checkmarks/checkmarks';
import { getCompleteStyleManualPositioning } from '~/utils/helpers';

interface LargeCardProps {
  block: {
    _template: string;
    container_width: string;
    container_height?: number;
    container_color?: string;
    font_color?: string;
    css_class_name: string;
    image_position: string;
    image: string;
    image_alt_text: string;
    manual_positioning: ManualPositioning;
    items?: {
      title: string;
      icon: string;
      text: string;
      cta_field: {
        cta_text: string;
        url: string;
        color: string;
      };
    }[];
    text_with_blocks?: {
      blocks: {
        _template: string;
        columns_bootstrap_rule: string;
        checkmarks_align_left: boolean;
        margin_top: number;
        margin_bottom: number;
        checkmarks: {
          name: string;
          text: string;
        }[];
        text: string;
        icon: string;
        title: string;
        title_font_size: number;
        text_font_size: number;
        cta_field: {
          text: string;
          url: string;
          color: string;
        };
        [key: string]: any;
      }[];
      [key: string]: any;
    }[];
  };
}

const LargeCard = ({ block }: LargeCardProps) => {
  const { isDesktop } = useViewport();
  const { manual_positioning } = block;
  let { mobile, desktop } =
    getCompleteStyleManualPositioning(manual_positioning);

  const hasContactForm = () => {
    // There should be a better way to style elements when their children is a form
    if (!block.text_with_blocks) {
      return false;
    }
    return block.text_with_blocks.some(twb =>
      twb.blocks.some(
        block => block._template === 'block__contact_form_package'
      )
    );
  };

  let hasContactFormClass = hasContactForm() ? ' has-contact-form' : '';

  const imageStyle =
    block.image_position === 'Left'
      ? {
          left: '-8rem',
          right: 'unset',
          transform: 'scaleX(-1)',
          ...(isDesktop ? desktop : mobile),
        }
      : { ...(isDesktop ? desktop : mobile) };

  let default_width = block.image_position === 'Left' ? 'col-lg-7' : 'col-lg-6';
  let default_offset = block.image_position === 'Left' ? 'offset-lg-5' : '';

  const cardStyle = {
    backgroundColor: block.container_color,
    color: block.font_color,
    height:
      block.container_height && isDesktop
        ? `${block.container_height}rem`
        : 'auto',
  };

  return (
    <section
      className={`block ${block._template} ${block.css_class_name ? block.css_class_name : ''} ${block.container_width === 'Full width' ? 'full-width' : ''} ${hasContactFormClass}`}
    >
      <div className={`container`}>
        <div
          className='card'
          style={cardStyle}
        >
          <div
            className='row'
            style={block.image_position === 'Left' ? { marginInline: 0 } : {}}
          >
            <div
              className={`${block.container_width === 'Full width' ? 'col-12' : default_width} ${block.image_position === 'Left' ? default_offset : ''}`}
            >
              {block.items &&
                block.items.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className='content-wr'
                    >
                      {item.title && (
                        <h3 style={item.icon ? { display: `flex` } : {}}>
                          {item.icon && (
                            <img
                              src={item.icon}
                              alt={item.title}
                              title={item.title}
                            />
                          )}
                          {item.title}
                        </h3>
                      )}
                      {item.text && (
                        <div
                          className='text-formatted'
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        ></div>
                      )}
                      {item.cta_field && <PartialCTA data={item.cta_field} />}
                    </div>
                  );
                })}

              {block.text_with_blocks &&
                block.text_with_blocks.map((twb, index) => {
                  let nested = twb.blocks.map((block, index) => {
                    switch (block._template) {
                      case 'partial__text':
                        return (
                          <PartialText
                            key={index}
                            block={block}
                          />
                        );
                      case 'partial__text_with_icon':
                        return (
                          <PartialTextWithIcon
                            key={index}
                            block={block}
                          />
                        );
                      case 'partial__features':
                        return (
                          <PartialFeatures
                            key={index}
                            block={block}
                          />
                        );
                      case 'partial__cta':
                        return (
                          <TwbCTA
                            key={index}
                            block={block}
                          />
                        );
                      case 'block__table_simple':
                        return (
                          <TableSimple
                            key={index}
                            block={block}
                          />
                        );
                      case 'block__contact_form_package':
                        return (
                          <ContactFormWrapper
                            key={index}
                            block={block}
                          />
                        );
                      case 'block__checkmarks':
                        return (
                          <Checkmarks
                            key={index}
                            alignLeft={block.checkmarks_align_left}
                            marginTop={block.margin_top}
                            marginBottom={block.margin_bottom}
                            checkmarks={block.checkmarks || []}
                            gap={block.gap}
                            columns_bootstrap_rule={
                              block.columns_bootstrap_rule
                            }
                          />
                        );
                      default:
                        return 'Missing block.';
                    }
                  });
                  return <React.Fragment key={index}>{nested}</React.Fragment>;
                })}
            </div>
          </div>

          {block.image && (
            <img
              className='card-image'
              src={block.image}
              alt={block.image_alt_text}
              title={block.image_alt_text}
              style={imageStyle}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default LargeCard;
